import { useCallback, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  FullPageSpinner,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Tab,
  Tabs,
  useTabParam,
} from '@structural-climbing/ui'
import { competitionBoards, disciplineForSection } from '@structural-climbing/validation'
import { useGymContext } from '../contexts/GymContext'
import { RoundsTab } from '../features/competitions/RoundsTab'
import { SessionsTab } from '../features/competitions/SessionsTab'
import { CategoriesTab } from '../features/competitions/CategoriesTab'
import { RoutesTab } from '../features/competitions/RoutesTab'
import { CompetitorsTab } from '../features/competitions/CompetitorsTab'
import { ResultsTab } from '../features/competitions/ResultsTab'
import { ReviewRoutesTab } from '../features/competitions/ReviewRoutesTab'
import { ScorecardTab } from '../features/competitions/ScorecardTab'
import { FinalStandingsTab } from '../features/competitions/FinalStandingsTab'
import { AboutStep, EntryStep, WhenStep } from '../features/competitions/setup/FormSteps'
import { PublishStep } from '../features/competitions/setup/PublishStep'
import { StagesBar } from '../features/competitions/setup/StagesBar'
import { StepPanel } from '../features/competitions/setup/StepPanel'
import { StepRail, type RailStep } from '../features/competitions/setup/StepRail'
import { stepLabel } from '../features/competitions/setup/stepLabel'
import {
  AFTER_TABS,
  LIVE_TABS,
  SETUP_STEPS,
  STAGES,
  legacyTab,
  stepDone,
  type AfterTab,
  type LiveTab,
  type SetupStep,
  type Stage,
} from '../features/competitions/setup/steps'
import { useCompetitionForm } from '../features/competitions/setup/useCompetitionForm'
import {
  competitionState,
  gymLocalNow,
  type CompetitionDetail,
  type CompetitionEntry,
  type CompetitionResult,
} from '../features/competitions/types'
import type { GymSection } from '../features/routes/types'

/** Where a click wanted to go while the Routes step held unsaved changes. */
interface Nav {
  stage: Stage
  step?: SetupStep
  live?: LiveTab
  after?: AfterTab
}

/**
 * One competition, in three stages: Setup (seven steps, the first three of
 * which created it), Running (competitors, results, scorecard) and After
 * (final standings, route review).
 *
 * It opens on the stage that matches where the competition is: a draft on
 * setup, a published one on running, an ended one on after.
 */
export function CompetitionDetailPage() {
  const { competitionId } = useParams<{ competitionId: string }>()
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const { slug, timezone } = useGymContext()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const [searchParams, setSearchParams] = useSearchParams()

  const [competition, setCompetition] = useState<CompetitionDetail | null>(null)
  const [entries, setEntries] = useState<CompetitionEntry[]>([])
  const [results, setResults] = useState<CompetitionResult[]>([])
  const [sections, setSections] = useState<GymSection[]>([])
  const [loading, setLoading] = useState(true)
  // Staged round and number changes on the Routes step. Leaving it unmounts it
  // and drops them, so leaving waits on a confirm while any are pending.
  const [routesDirty, setRoutesDirty] = useState(false)
  const [pendingNav, setPendingNav] = useState<Nav | null>(null)
  // Set by "Show competitors" on a category that cannot be removed yet.
  const [competitorCategory, setCompetitorCategory] = useState<string | null>(null)

  const form = useCompetitionForm(competition)

  const localNow = gymLocalNow(timezone)
  const state = competition ? competitionState(competition, localNow) : 'upcoming'
  // Where the page opens when the URL names no stage or step, decided once on
  // the first load: recomputing it would jump a gym on to the next step the
  // moment it finished the one it was on.
  const [landing, setLanding] = useState<{ stage: Stage; step: SetupStep } | null>(null)
  useEffect(() => {
    if (!competition || landing) return
    const s = competitionState(competition, gymLocalNow(timezone))
    setLanding({
      stage: !competition.published ? 'setup' : s === 'ended' ? 'after' : 'live',
      step: SETUP_STEPS.find((x) => !stepDone(x, competition)) ?? 'about',
    })
  }, [competition, landing, timezone])
  const defaultStage: Stage = landing?.stage ?? 'setup'
  const firstOpenStep: SetupStep = landing?.step ?? 'about'

  const [stageParam] = useTabParam<Stage>('stage', STAGES, defaultStage)
  const [step] = useTabParam<SetupStep>('step', SETUP_STEPS, firstOpenStep)
  const [live] = useTabParam<LiveTab>('live', LIVE_TABS, 'competitors')
  const [after] = useTabParam<AfterTab>('after', AFTER_TABS, 'final')
  // Running and After have nothing to show before publishing.
  const stage: Stage = competition && !competition.published ? 'setup' : stageParam

  // A link from before the redesign carries ?tab=; send it to where that tab
  // lives now, once, and drop the old key.
  useEffect(() => {
    const target = legacyTab(searchParams.get('tab'))
    if (!searchParams.has('tab')) return
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('tab')
        if (target) {
          next.set('stage', target.stage)
          if (target.step) next.set('step', target.step)
          if (target.live) next.set('live', target.live)
          if (target.after) next.set('after', target.after)
        }
        return next
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams])

  const load = useCallback(async () => {
    if (!competitionId) return
    try {
      const token = await getAccessTokenSilently()
      const headers = { Authorization: `Bearer ${token}` }
      const [detailRes, entriesRes, resultsRes, sectionsRes] = await Promise.all([
        fetch(`${apiUrl}/competitions/${competitionId}`, { headers }),
        fetch(`${apiUrl}/competitions/${competitionId}/entries`, { headers }),
        fetch(`${apiUrl}/competitions/${competitionId}/results`, { headers }),
        fetch(`${apiUrl}/gym-sections`, { headers }),
      ])
      if (detailRes.ok) setCompetition((await detailRes.json()) as CompetitionDetail)
      if (entriesRes.ok) setEntries((await entriesRes.json()) as CompetitionEntry[])
      if (resultsRes.ok) setResults((await resultsRes.json()) as CompetitionResult[])
      if (sectionsRes.ok) setSections((await sectionsRes.json()) as GymSection[])
    } finally {
      setLoading(false)
    }
  }, [apiUrl, competitionId, getAccessTokenSilently])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * One write for every key that changes. Two setSearchParams calls in the same
   * tick each start from the same old params, so the second would undo the first.
   */
  function apply(nav: Nav) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('stage', nav.stage)
        if (nav.step) next.set('step', nav.step)
        if (nav.live) next.set('live', nav.live)
        if (nav.after) next.set('after', nav.after)
        return next
      },
      { replace: true },
    )
  }

  function navigateTo(nav: Nav) {
    const leavingRoutes =
      stage === 'setup' && step === 'routes' && !(nav.stage === 'setup' && nav.step === 'routes')
    if (leavingRoutes && routesDirty) setPendingNav(nav)
    else apply(nav)
  }

  function discardRouteChanges() {
    if (!pendingNav) return
    setRoutesDirty(false)
    apply(pendingNav)
    setPendingNav(null)
  }

  // Held until the landing stage is known, so the page never flashes setup first.
  if (loading || (competition && !landing)) return <FullPageSpinner />
  if (!competition) {
    return (
      <div className="p-4 sm:p-8">
        <p className="text-foreground/60">{t('competitions.notFound')}</p>
      </div>
    )
  }

  const ended = state === 'ended'
  const single = competition.format === 'single_day'
  // Both walls' sections for a combined competition.
  const boards = competitionBoards(competition.discipline)
  const disciplineSections = sections.filter((s) =>
    boards.includes(disciplineForSection(s.discipline)),
  )
  const doneCount = SETUP_STEPS.filter((s) => stepDone(s, competition)).length
  const railSteps: RailStep[] = SETUP_STEPS.map((key) => ({
    key,
    label:
      key === 'publish' && competition.published
        ? t('competitionSetup.stepPublished')
        : stepLabel(key, competition.format, t),
    done: stepDone(key, competition),
  }))
  const nextStep = (s: SetupStep) => SETUP_STEPS[SETUP_STEPS.indexOf(s) + 1] ?? s
  const prevStep = (s: SetupStep) => SETUP_STEPS[SETUP_STEPS.indexOf(s) - 1]
  const goStep = (s: SetupStep) => navigateTo({ stage: 'setup', step: s })

  /** Steps 1–3 save the whole form, then move on. */
  async function saveAndNext(from: SetupStep) {
    const result = await form.save()
    if (!result.ok) {
      if (result.step) goStep(result.step)
      return
    }
    await load()
    goStep(nextStep(from))
  }

  const back = prevStep(step) ? () => goStep(prevStep(step)!) : undefined
  const continueTo = {
    label: t('competitionSetup.continue'),
    onPress: () => goStep(nextStep(step)),
  }

  function renderStep() {
    const c = competition!
    switch (step) {
      case 'about':
      case 'when':
      case 'entry':
        return (
          <StepPanel
            onBack={back}
            error={form.error}
            primary={{
              label: t('competitionSetup.continue'),
              onPress: () => void saveAndNext(step),
              isLoading: form.saving,
            }}
          >
            {step === 'about' && <AboutStep form={form} />}
            {step === 'when' && <WhenStep form={form} />}
            {step === 'entry' && <EntryStep form={form} />}
          </StepPanel>
        )
      case 'categories':
        return (
          <StepPanel
            onBack={back}
            note={c.categories.length === 0 ? t('competitionSetup.categoriesNeedOne') : undefined}
            primary={{ ...continueTo, isDisabled: c.categories.length === 0 }}
          >
            <CategoriesTab
              competitionId={c.id}
              categories={c.categories}
              locked={ended}
              onRefresh={() => void load()}
              onShowCompetitors={(id) => {
                setCompetitorCategory(id)
                navigateTo({ stage: 'live', live: 'competitors' })
              }}
            />
          </StepPanel>
        )
      case 'slots':
        return (
          <StepPanel
            onBack={back}
            // Only while there are none: once one exists, "you can skip this"
            // no longer describes the screen.
            note={
              (single ? c.sessions : c.rounds).length > 0
                ? undefined
                : single
                  ? t('competitionSetup.groupsOptional')
                  : t('competitionSetup.roundsOptional')
            }
            primary={continueTo}
          >
            {single ? (
              <SessionsTab
                competitionId={c.id}
                startsAt={c.starts_at}
                endsAt={c.ends_at}
                sessions={c.sessions}
                locked={ended}
                onRefresh={() => void load()}
              />
            ) : (
              <RoundsTab
                competitionId={c.id}
                startsAt={c.starts_at}
                endsAt={c.ends_at}
                rounds={c.rounds}
                localNow={localNow}
                locked={ended}
                onRefresh={() => void load()}
              />
            )}
          </StepPanel>
        )
      case 'routes':
        return (
          <StepPanel onBack={back} primary={continueTo}>
            <RoutesTab
              competitionId={c.id}
              discipline={c.discipline}
              routes={c.routes}
              rounds={c.rounds}
              localNow={localNow}
              sections={disciplineSections}
              locked={ended}
              results={results}
              onRefresh={() => void load()}
              onDirtyChange={setRoutesDirty}
            />
          </StepPanel>
        )
      case 'publish':
        return (
          <PublishStep
            competition={c}
            onGoto={goStep}
            onBack={() => goStep('routes')}
            onChanged={load}
          />
        )
    }
  }

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <Link to={`/${slug}/competitions`} className="text-sm text-primary hover:underline">
            ← {t('competitions.backToList')}
          </Link>
          <h1 className="mt-1 text-2xl font-bold break-words">
            {competition.name}
            {!competition.published && (
              <span className="ml-2 inline-block rounded-full bg-default-100 px-2.5 py-0.5 align-middle text-xs font-medium text-foreground/70">
                {t('competitions.draft')}
              </span>
            )}
          </h1>
          <p className="text-sm text-foreground/60">
            {t(`competitions.state_${state}`)} ·{' '}
            {t(`competitions.discipline_${competition.discipline}`)} ·{' '}
            {t(`competitions.format_${competition.format}`)}
          </p>
        </div>
        <StagesBar
          stage={stage}
          onChange={(next) => navigateTo({ stage: next })}
          doneSteps={doneCount}
          published={competition.published}
          ended={ended}
        />
      </div>

      {stage === 'setup' && (
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-6">
          <StepRail
            steps={railSteps}
            current={step}
            onSelect={goStep}
            note={competition.published ? t('competitionSetup.setupDoneNote') : undefined}
          />
          {renderStep()}
        </div>
      )}

      {stage === 'live' && (
        <Tabs
          aria-label={t('competitionSetup.stageLive')}
          variant="underlined"
          selectedKey={live}
          onSelectionChange={(key) => navigateTo({ stage: 'live', live: key as LiveTab })}
          classNames={{
            base: 'max-w-full',
            tabList: 'max-w-full flex-nowrap overflow-x-auto scrollbar-hide',
          }}
        >
          <Tab key="competitors" title={`${t('competitions.tabCompetitors')} · ${entries.length}`}>
            <CompetitorsTab
              entries={entries}
              categories={competition.categories}
              sessions={competition.sessions}
              locked={ended}
              requiresConfirmation={competition.requires_confirmation}
              onRefresh={() => void load()}
              initialCategory={competitorCategory}
            />
          </Tab>
          <Tab key="results" title={t('competitions.tabResults')}>
            <ResultsTab
              competitionId={competition.id}
              discipline={competition.discipline}
              sections={disciplineSections}
              results={results}
              entries={entries}
              routes={competition.routes}
              rounds={competition.rounds}
              onRefresh={() => void load()}
            />
          </Tab>
          <Tab key="scorecard" title={t('scorecard.tab')}>
            <ScorecardTab
              competitionId={competition.id}
              categories={competition.categories}
              entries={entries}
              version={results}
            />
          </Tab>
        </Tabs>
      )}

      {stage === 'after' && (
        <Tabs
          aria-label={t('competitionSetup.stageAfter')}
          variant="underlined"
          selectedKey={after}
          onSelectionChange={(key) => navigateTo({ stage: 'after', after: key as AfterTab })}
          classNames={{
            base: 'max-w-full',
            tabList: 'max-w-full flex-nowrap overflow-x-auto scrollbar-hide',
          }}
        >
          <Tab key="final" title={t('competitionSetup.finalTitle')}>
            <FinalStandingsTab
              competitionId={competition.id}
              categories={competition.categories}
              competitorCount={entries.length}
              routeCount={competition.routes.length}
            />
          </Tab>
          <Tab key="review" title={t('competitions.tabReview')}>
            <ReviewRoutesTab
              discipline={competition.discipline}
              sections={disciplineSections}
              ended={ended}
              labels={Object.fromEntries(
                competition.routes.map((r, i) => [r.route_id, r.label ?? String(i + 1)]),
              )}
              rounds={competition.rounds}
              localNow={localNow}
              roundIds={Object.fromEntries(competition.routes.map((r) => [r.route_id, r.round_id]))}
              routes={competition.provenance_routes}
              onRefresh={() => void load()}
            />
          </Tab>
        </Tabs>
      )}

      <Modal
        scrollBehavior="inside"
        isOpen={pendingNav !== null}
        onClose={() => setPendingNav(null)}
      >
        <ModalContent>
          <ModalHeader>{t('competitions.unsavedTitle')}</ModalHeader>
          <ModalBody className="text-sm">
            <p>{t('competitions.unsavedBody')}</p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setPendingNav(null)}>
              {t('competitions.keepEditing')}
            </Button>
            <Button color="danger" onPress={discardRouteChanges}>
              {t('competitions.discardAndLeave')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
